import { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import LicensedModule from "models/LicensedModule";
import type Question from "models/Question";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { computed, makeObservable, observable, runInAction } from "mobx";
import Classifier from "models/ClassifierModel";
import type { MappedCustomerType } from "models/CustomerType";
import AgentService from "services/AgentService";
import ClassifierService from "services/ClassifierService";
import CustomerTypesService from "services/CustomerTypesService";
import { ModuleService } from "services/ModuleService";
import { TopicService } from "services/TopicService";
import { AuthStore } from "stores/AuthStore";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { serializeToUtc } from "utils/DateTimeUtils";
import {
    ApplicationFiltersStore,
    PrimaryDriver,
    PrimaryTopic,
    SimpleTheme,
    SimpleTopicCluster,
} from "./ApplicationFiltersStore";
import LLMClassifierService from "services/LLMClassifierService";
import { SlimLLMClassifierResponse } from "models/LLMs";
import Agentv2 from "models/Agentv2";
import AgentManager from "models/AgentManager";
import { ExtendedMetadataGroupedList } from "components/ApplicationFilters/FiltersAndChips/ExtendedMetadata/ExtendedMetadataGroupedFilter";
import { SignalsService } from "services/SignalsService";

const getValidExtendedMetadataResult = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;

    const trimmedValue = value.trim();
    return trimmedValue.length ? trimmedValue : undefined;
};

type NestedQuestion = Question & { questions?: NestedQuestion[] | null };

/**
 * `GetLicensedModule` returns questions as a tree: only top-level questions sit on the module, and
 * a Question Group's children are nested under its `questions`. Flattens that tree depth first, so
 * each child follows its parent, giving the Question Result filter and grouping every question
 * rather than only the top level.
 */
export const flattenModuleQuestions = (
    questions: NestedQuestion[] | null | undefined,
): Question[] =>
    (questions ?? []).flatMap((question) => [
        question,
        ...flattenModuleQuestions(question.questions),
    ]);

@AcxStore
export class ApplicationFilterValuesStore extends BaseStore {
    public static Tasks = {
        LOAD_TOPICS: "Load Topics",
        LOAD_DRIVER_THEMES: "Load Driver Themes",
        LOAD_PRIMARY_TOPICS: "Load Primary Topics",
        LOAD_PRIMARY_DRIVERS: "Load Primary Drivers",
        LOAD_USER_CLASSIFIERS: "Load User Classifiers",
        LOAD_LICENSED_MODULES: "Load Licensed Modules",
        LOAD_LICENSED_HIERARCHY_MODULES: "Load Licensed Hierarchy Modules",
        LOAD_DATA_LABELING_MODULES: "Load Data Labeling Modules",
        LOAD_QUESTION_RESULT_MODULES: "Load Question Results Modules",
        LOAD_MODULE_DETAILS: "Load Module Details",
        LOAD_MAPPED_CUSTOMER_TYPES: "Load Mapped Customer Types",
        GET_AGENT: "Get Agent",
        SEARCH_AGENTS: "Search Agents",
        LOAD_AGENTS: "Load Agents",
        LOAD_AGENT_MANAGERS: "Load Agent Managers",
        LOAD_LLM_CLASSIFIERS: "Load LLM Classifiers",
        LOAD_METADATA_FIELDS: "Load Metadata Fields",
        LOAD_EXTENDED_METADATA_GROUPED_LIST:
            "Load Extended Metadata Grouped List",
    } as const;

    private readonly applicationFiltersStore: ApplicationFiltersStore;
    private readonly messageStore: MessageStore;
    private readonly authStore: AuthStore;

    private readonly signalsService = new SignalsService();
    private readonly agentService = new AgentService();
    private readonly classifierService = new ClassifierService();
    private readonly llmClassifierService = new LLMClassifierService();
    private readonly customerTypesService = new CustomerTypesService();
    private readonly moduleService = new ModuleService();
    private readonly topicsService = new TopicService();

    @observable
    topicClusters: SimpleTopicCluster[] | undefined;

    private loadedTopicsKey: string | undefined;

    @observable
    driverThemes: SimpleTheme[] | undefined;

    private loadedDriverThemesKey: string | undefined;

    @observable
    primaryTopics: PrimaryTopic[] | undefined;

    @observable
    primaryDrivers: PrimaryDriver[] | undefined;

    @observable
    userClassifiers: Classifier[] | undefined;

    @observable
    llmClassifiers: SlimLLMClassifierResponse[] | undefined;

    @observable
    mappedCustomerTypes: MappedCustomerType[] = [];

    @computed get contactTypeOptions() {
        return this.mappedCustomerTypes.map((mapping) => ({
            label: mapping.name,
            value: mapping.sourceName,
        }));
    }

    @observable
    licensedModules: { id: string; title: string }[] | undefined;

    /**
     * Every hierarchy-scoped licensed module, including ones this viewer cannot access.
     * `moduleCategory` is what says whether a module's content is restricted, so the inaccessible
     * entries have to be here for {@link canUseModuleScoreModule} to answer about them at all.
     */
    @observable
    hierarchyLicensedModules:
        | { id: string; title: string; moduleCategory?: { name?: string } }[]
        | undefined;

    @observable
    dataLabelingModules: ModulePreview[] = [];

    /**
     * Every module the Question Results filter may reference, including ones this
     * viewer cannot access. Permission checks need the inaccessible entries too:
     * a saved filter can name a module the receiving user has no rights to, and
     * only this listing can say which category it belongs to.
     */
    @observable
    questionResultLicensedModules: ModulePreview[] = [];

    /** The subset of the above this viewer may pick from, sorted for the dropdown. */
    @observable
    questionResultFilterableModules: ModulePreview[] = [];

    /**
     * Organization the two listings above were loaded for, or undefined before the
     * first load. Doubles as the loaded flag and as the cache key that forces a
     * refetch after an org switch, so a stale catalog can never decide a permission
     * question for the wrong tenant.
     */
    @observable
    questionResultModulesLoadedForOrgId?: string;

    @observable
    moduleDetailsById: Map<string, LicensedModule> = new Map();

    @observable
    extendedMetadataFields: { label: string; value: string }[] = [];

    @observable
    extendedMetadataGroupedLists: Record<
        string,
        ExtendedMetadataGroupedList[]
    > = {};

    @observable
    agents: Agentv2[] = [];

    @observable
    agentManagers: AgentManager[] = [];

    constructor(private rootStore: IRootStore) {
        super("Application Filter Values");
        makeObservable(this);

        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        this.messageStore = rootStore.getStore(MessageStore);
        this.authStore = rootStore.getStore(AuthStore);

        this.loadUserClassifiers();
        this.loadMappedCustomerTypes();
        if (this.authStore.canUserView("Topics")) {
            this.loadPrimaryTopics();
        }
        if (this.authStore.canUserView("Drivers")) {
            this.loadPrimaryDrivers();
        }
        this.loadAgents();
        this.loadAgentManagers();
        this.loadHierarchyModules();
    }

    /**
     * Loads topic clusters (themes) once per selected date range.
     * @returns
     */
    async loadTopics() {
        const startDate = serializeToUtc(
            this.applicationFiltersStore.startDate ??
                this.applicationFiltersStore.quickFiltersStore.startDate,
        )!;
        const endDate = serializeToUtc(
            this.applicationFiltersStore.endDate ??
                this.applicationFiltersStore.quickFiltersStore.endDate,
        )!;
        const topicsKey = `${startDate}:${endDate}`;

        if (
            this.loadedTopicsKey === topicsKey ||
            this.getTaskLoading(ApplicationFilterValuesStore.Tasks.LOAD_TOPICS)
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_TOPICS,
            async () => {
                try {
                    const topics = await this.signalsService.getAllThemes(
                        startDate,
                        endDate,
                    );
                    runInAction(() => {
                        this.topicClusters = topics;
                        this.loadedTopicsKey = topicsKey;
                    });
                } catch (error) {
                    this.topicClusters = undefined;
                    this.messageStore.logError(
                        "Failed to retrieve themes. Please try again.",
                    );
                }
            },
        );
    }

    /**
     * Loads Themes resolved through the Primary Driver, once per selected date range. This is the
     * catalog for the "themes" filter; `loadTopics` is the legacy Fast Reporting equivalent.
     */
    async loadDriverThemes() {
        const startDate = serializeToUtc(
            this.applicationFiltersStore.startDate ??
                this.applicationFiltersStore.quickFiltersStore.startDate,
        )!;
        const endDate = serializeToUtc(
            this.applicationFiltersStore.endDate ??
                this.applicationFiltersStore.quickFiltersStore.endDate,
        )!;
        const driverThemesKey = `${startDate}:${endDate}`;

        if (
            this.loadedDriverThemesKey === driverThemesKey ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_DRIVER_THEMES,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_DRIVER_THEMES,
            async () => {
                try {
                    const themes =
                        await this.signalsService.getAllDriverThemes(
                            startDate,
                            endDate,
                        );
                    runInAction(() => {
                        this.driverThemes = themes;
                        this.loadedDriverThemesKey = driverThemesKey;
                    });
                } catch (error) {
                    this.driverThemes = undefined;
                    this.messageStore.logError(
                        "Failed to retrieve themes. Please try again.",
                    );
                }
            },
        );
    }

    async loadPrimaryTopics() {
        if (!this.authStore.canUserView("Topics")) return;

        if (
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_TOPICS,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_TOPICS,
            async () => {
                try {
                    const primaryTopics =
                        await this.topicsService.getPrimaryTopicsByOrganization(
                            this.authStore.orgStore.selectedOrganization?.id ??
                                "",
                        );
                    runInAction(() => {
                        this.primaryTopics = primaryTopics;
                    });
                } catch (error) {
                    this.primaryTopics = undefined;
                    this.messageStore.logError(
                        "Failed to retrieve topics. Please try again.",
                    );
                }
            },
        );
    }

    async loadPrimaryDrivers() {
        if (!this.authStore.canUserView("Drivers")) return;

        if (
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_DRIVERS,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_DRIVERS,
            async () => {
                try {
                    const primaryDrivers =
                        await this.topicsService.getDriversByOrganization(
                            this.authStore.orgStore.selectedOrganization?.id ??
                                "",
                        );
                    runInAction(() => {
                        this.primaryDrivers = primaryDrivers;
                    });
                } catch (error) {
                    this.primaryDrivers = undefined;
                    this.messageStore.logError(
                        "Failed to retrieve drivers. Please try again.",
                    );
                }
            },
        );
    }

    async loadUserClassifiers() {
        this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_USER_CLASSIFIERS,
            async () => {
                try {
                    const classifiers =
                        await this.classifierService.getUserClassifiers(
                            this.authStore.orgStore.selectedOrganization?.id,
                            true,
                        );
                    runInAction(() => {
                        this.userClassifiers = classifiers;
                    });
                } catch (err: any) {
                    this.messageStore.logError(
                        "Failed to load classifiers: " + JSON.stringify(err),
                    );
                }
            },
        );
    }

    async loadLLMClassifiers() {
        // Returned so callers that need the catalog before deciding something can await it,
        // the same way loadQuestionResultFilterableModules does.
        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_LLM_CLASSIFIERS,
            async () => {
                try {
                    const llmClassifiers =
                        await this.llmClassifierService.getSlimLLMClassifiers();
                    runInAction(() => {
                        this.llmClassifiers = llmClassifiers;
                    });
                } catch (err: any) {
                    this.messageStore.logError(
                        "Failed to load llm classifiers: " +
                            JSON.stringify(err),
                    );
                }
            },
        );
    }

    async loadMappedCustomerTypes() {
        this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_MAPPED_CUSTOMER_TYPES,
            async () => {
                try {
                    const mappedCustomerTypes =
                        await this.customerTypesService.getMappedCustomerTypes();
                    runInAction(() => {
                        this.mappedCustomerTypes = mappedCustomerTypes;
                    });
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to load contact type labels: " +
                            JSON.stringify(error),
                    );
                }
            },
        );
    }

    async loadModules() {
        if (
            !!this.licensedModules ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_MODULES,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_MODULES,
            async () => {
                try {
                    const modules =
                        await this.moduleService.getLicensedModules();

                    runInAction(() => {
                        this.licensedModules = modules;
                    });
                } catch (error) {
                    this.licensedModules = undefined;
                    this.messageStore.logError(
                        "Failed to retrieve modules. Please try again.",
                    );
                }
            },
        );
    }

    async loadHierarchyModules() {
        if (
            !!this.hierarchyLicensedModules ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks
                    .LOAD_LICENSED_HIERARCHY_MODULES,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_HIERARCHY_MODULES,
            async () => {
                try {
                    const modules =
                        await this.moduleService.getHierarchyLicensedModules();

                    runInAction(() => {
                        this.hierarchyLicensedModules = modules;
                    });
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to retrieve modules. Please try again.",
                    );
                }
            },
        );
    }

    async loadDataLabelingModules() {
        if (
            this.dataLabelingModules.length !== 0 ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_DATA_LABELING_MODULES,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_DATA_LABELING_MODULES,
            async () => {
                const res =
                    await this.moduleService.getConversationLicensedModules();
                runInAction(() => {
                    const canViewDataLabelingModules =
                        this.authStore.canUserView("Data Labeling");
                    const canViewResearchModules =
                        this.authStore.canUserView("Research");

                    const dataLabelingModules = res.filter(
                        (module) =>
                            module.moduleCategory.name === "Data Labeling" &&
                            canViewDataLabelingModules,
                    );
                    const researchModules = res.filter(
                        (module) =>
                            module.moduleCategory.name === "Research" &&
                            canViewResearchModules,
                    );

                    this.dataLabelingModules = [
                        ...dataLabelingModules,
                        ...researchModules,
                    ];
                });
            },
        );
    }

    /**
     * Loads modules that may be referenced by the Question Results filter.
     * Returns conversation and hierarchy modules whose category the user can
     * view based on their permissions (Data Labeling, Research, Safety Event Review).
     * Modules outside these categories are excluded.
     */
    async loadQuestionResultFilterableModules() {
        if (
            this.hasLoadedQuestionResultLicensedModules ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_QUESTION_RESULT_MODULES,
            )
        )
            return;

        const loadingForOrgId = this.currentOrganizationId;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_QUESTION_RESULT_MODULES,
            async () => {
                const res =
                    await this.moduleService.getQuestionResultLicensedModules();
                runInAction(() => {
                    this.questionResultLicensedModules = res;
                    this.questionResultFilterableModules = res
                        .filter((module) =>
                            this.canUseQuestionResultModule(module.id),
                        )
                        .sort((left, right) =>
                            left.name.localeCompare(right.name),
                        );
                    this.questionResultModulesLoadedForOrgId = loadingForOrgId;
                });
            },
        );
    }

    // orgStore is only assigned once auth bootstraps, and this getter feeds a
    // computed read during render, so it must tolerate being asked too early.
    private get currentOrganizationId(): string {
        return this.authStore.orgStore?.selectedOrganization?.id ?? "";
    }

    /**
     * Whether the Question Result module catalog is loaded and belongs to the
     * organization currently selected. False both before the first load and for
     * the window after an org switch, which is what callers key permission
     * decisions off: without a catalog there is no category to check, so they
     * leave selections alone rather than strip them on a guess.
     */
    @computed
    public get hasLoadedQuestionResultLicensedModules(): boolean {
        return (
            this.questionResultModulesLoadedForOrgId ===
            this.currentOrganizationId
        );
    }

    /**
     * Cheap key that changes exactly when the answers from
     * {@link canUseQuestionResultModule} can change: on load completion and on an
     * org switch. Memoized consumers of the filter sanitizer depend on this so
     * their cached output recomputes once the catalog lands.
     */
    @computed
    public get questionResultModuleAccessKey(): string {
        return this.questionResultModulesLoadedForOrgId ?? "pending";
    }

    /**
     * Whether a Question Result selection naming this module may be applied.
     * Restrict Evaluations hides the entire filter for non-ultra users; otherwise
     * this mirrors the category rules the dropdown itself filters by, and leaves
     * unknown module ids alone so a stale or deleted module behaves as it did
     * before permissions were considered.
     */
    public canUseQuestionResultModule(licensedModuleId: string): boolean {
        if (
            this.authStore.canUserView("Restrict Evaluations") &&
            !this.authStore.isUserUltra()
        ) {
            return false;
        }

        const module = this.questionResultLicensedModules.find(
            (item) => item.id === licensedModuleId,
        );

        switch (module?.moduleCategory?.name) {
            case "Data Labeling":
                return this.authStore.canUserView("Data Labeling");
            case "Research":
                return this.authStore.canUserView("Research");
            case "Safety Event Review":
                return this.authStore.canUserView("Safety Event Review");
            default:
                return true;
        }
    }

    /**
     * Whether a Module Score selection naming this module may be applied.
     *
     * The module score sibling of {@link canUseQuestionResultModule}, and the same rules, because a
     * chart grouped by a module's score shows the data the module score filter would: Restrict
     * Evaluations hides it outright for non-ultra users, and otherwise a restricted category needs
     * its own permission. An unknown module id is left alone, so a stale or deleted module behaves
     * as it did before permissions were considered.
     *
     * The "Module Scores" permission is not checked here. It gates the whole dimension rather than
     * one module, so `ApplicationFilterItemsStore.canAccessFilterItem` applies it once, for the
     * filter and the group-by menu alike.
     */
    public canUseModuleScoreModule(licensedModuleId: string): boolean {
        if (
            this.authStore.canUserView("Restrict Evaluations") &&
            !this.authStore.isUserUltra()
        ) {
            return false;
        }

        const module = (this.hierarchyLicensedModules ?? []).find(
            (item) => item.id === licensedModuleId,
        );

        switch (module?.moduleCategory?.name) {
            case "Data Labeling":
                return this.authStore.canUserView("Data Labeling");
            case "Research":
                return this.authStore.canUserView("Research");
            case "Safety Event Review":
                return this.authStore.canUserView("Safety Event Review");
            default:
                return true;
        }
    }

    /**
     * The modules a Module Score grouping may be configured with, in load order.
     *
     * Derived rather than stored, unlike the Question Result pair, because the underlying list is
     * shared with the module score filter and narrowing it in place would change which modules that
     * filter offers.
     */
    @computed
    public get moduleScoreFilterableModules(): {
        id: string;
        title: string;
    }[] {
        return (this.hierarchyLicensedModules ?? []).filter((module) =>
            this.canUseModuleScoreModule(module.id),
        );
    }

    /**
     * Lazily loads (and caches) the full LicensedModule for the given id so
     * the Question Results filter can resolve questions and answer tags.
     */
    async loadModuleDetails(licensedModuleId: string) {
        if (!licensedModuleId) return;
        if (this.moduleDetailsById.has(licensedModuleId)) return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_MODULE_DETAILS,
            async () => {
                try {
                    const module = await this.moduleService.getLicensedModule(
                        licensedModuleId,
                    );
                    module.questions = flattenModuleQuestions(module.questions);
                    runInAction(() => {
                        this.moduleDetailsById.set(licensedModuleId, module);
                    });
                } catch (error) {
                    // Saved filters may reference modules the current user
                    // cannot access. Swallow the error so the filter still
                    // applies on the backend; the chip will fall back to a
                    // generic label.
                }
            },
        );
    }

    async loadAgents() {
        if (
            this.agents.length !== 0 ||
            this.getTaskLoading(ApplicationFilterValuesStore.Tasks.LOAD_AGENTS)
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_AGENTS,
            async () => {
                try {
                    const res = await this.agentService.getAgents(
                        this.authStore.orgStore.selectedOrganization?.id ?? "",
                    );

                    runInAction(() => {
                        this.agents = res;
                    });

                    return res;
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to load agents. Please try again.",
                    );
                }
            },
        );
    }

    async loadAgentManagers() {
        if (
            this.agentManagers.length !== 0 ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_AGENT_MANAGERS,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_AGENT_MANAGERS,
            async () => {
                try {
                    const res = await this.agentService.getAgentManagers(
                        this.authStore.orgStore.selectedOrganization?.id ?? "",
                    );

                    runInAction(() => {
                        this.agentManagers = res;
                    });

                    return res;
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to load agent managers. Please try again.",
                    );
                }
            },
        );
    }

    async loadMetadataFields() {
        if (
            this.extendedMetadataFields.length !== 0 ||
            this.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_METADATA_FIELDS,
            )
        )
            return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_METADATA_FIELDS,
            async () => {
                const response =
                    await this.classifierService.getExtendedMetadataStringClassifiers();
                runInAction(() => {
                    this.extendedMetadataFields = response
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((field) => ({
                            label: field.name,
                            value: field.name,
                        }));
                });
            },
        );
    }

    async loadExtendedMetadataGroupedList(key: string, classifierId: string) {
        if (this.extendedMetadataGroupedLists[key] || !classifierId) return;

        return this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks
                .LOAD_EXTENDED_METADATA_GROUPED_LIST,
            async () => {
                try {
                    const response =
                        await this.classifierService.getClassifierOutputs(
                            classifierId,
                        );
                    runInAction(() => {
                        this.extendedMetadataGroupedLists[key] = response
                            .map((output) => {
                                const result = getValidExtendedMetadataResult(
                                    output.result,
                                );
                                if (!result) return undefined;

                                return {
                                    ...output,
                                    result,
                                    classifierName: key,
                                };
                            })
                            .filter(
                                (
                                    output,
                                ): output is ExtendedMetadataGroupedList =>
                                    !!output,
                            );
                    });
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to load extended metadata list. Please try again.",
                    );
                }
            },
        );
    }

    getContactTypeLabel(sourceContactType: string) {
        const customerType = this.mappedCustomerTypes.find(
            (c) => c.sourceName === sourceContactType,
        );

        if (!customerType) return sourceContactType;
        return customerType.name;
    }

    searchAgent = async (inputValue: string) => {
        const agents = await this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.SEARCH_AGENTS,
            async () => {
                try {
                    return await this.agentService.searchAgents(
                        this.authStore.orgStore.selectedOrganization?.id,
                        inputValue,
                    );
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to search agents. Please try again.",
                    );
                    return [];
                }
            },
        );

        if (agents === AsyncTaskStatus.Error) return [];

        return agents;
    };

    searchPrimaryTopic = async (inputValue: string) => {
        const primaryTopics = await this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_TOPICS,
            async () => {
                try {
                    return await this.topicsService.searchTopics(inputValue);
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to search primary topics. Please try again.",
                    );
                    return [];
                }
            },
        );

        if (primaryTopics === AsyncTaskStatus.Error) return [];

        return primaryTopics;
    };

    searchPrimaryDriver = async (inputValue: string) => {
        const primaryDrivers = await this.setupAsyncTask(
            ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_DRIVERS,
            async () => {
                try {
                    return await this.topicsService.searchDrivers(inputValue);
                } catch (error) {
                    this.messageStore.logError(
                        "Failed to search primary drivers. Please try again.",
                    );
                    return [];
                }
            },
        );

        if (primaryDrivers === AsyncTaskStatus.Error) return [];

        return primaryDrivers;
    };

    @computed
    get organizedConversationLabelingOptions() {
        const conversationLabelToOptionMap = {};
        this.dataLabelingModules.forEach((module) => {
            const moduleCategory = module.moduleCategory.name;
            if (!conversationLabelToOptionMap[moduleCategory]) {
                conversationLabelToOptionMap[moduleCategory] = [
                    { name: module.name, id: module.id },
                ];
            } else {
                conversationLabelToOptionMap[moduleCategory].push({
                    name: module.name,
                    id: module.id,
                });
            }
        });

        const categories = Object.keys(conversationLabelToOptionMap);

        if (categories.length === 0) {
            return [];
        } else if (categories.length === 1) {
            return conversationLabelToOptionMap[categories[0]];
        } else {
            return categories.map((category) => ({
                label: category,
                options: conversationLabelToOptionMap[category],
            }));
        }
    }
}
